import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import { nivelAcessoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// =====================================================
// INTERFACES TYPESCRIPT
// =====================================================

interface MenuPermissions {
  menu_dashboard: boolean;
  menu_clientes: boolean;
  menu_agendamento: boolean;
  menu_evolucoes: boolean;
  menu_experiencia: boolean;
  menu_financeiro: boolean;
  menu_recebimentos: boolean;
  menu_despesas: boolean;
  menu_tipos_despesa: boolean;
  menu_raiox_financeiro: boolean;
  menu_utilidades: boolean;
  menu_gerar_mensalidades: boolean;
  menu_gerar_agenda: boolean;
  menu_configuracoes: boolean;
  menu_usuarios: boolean;
  menu_niveis_acesso: boolean;
  menu_notificacoes: boolean;
  menu_logs: boolean;
}

interface NivelAcesso extends MenuPermissions {
  id: number;
  nome: string; // campo 'grupo' do banco
  descricao: string; // campo 'detalhes' do banco
  nivel_acesso: string;
  ativo: boolean;
}

interface FormData extends MenuPermissions {
  nome: string;
  nivel_acesso: string;
  descricao: string;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const NiveisAcesso: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados principais
  const [niveisAcesso, setNiveisAcesso] = useState<NivelAcesso[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estados do formulário
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    nivel_acesso: '',
    descricao: '',
    menu_dashboard: true,
    menu_clientes: true,
    menu_agendamento: true,
    menu_evolucoes: true,
    menu_experiencia: true,
    menu_financeiro: true,
    menu_recebimentos: true,
    menu_despesas: true,
    menu_tipos_despesa: true,
    menu_raiox_financeiro: true,
    menu_utilidades: true,
    menu_gerar_mensalidades: true,
    menu_gerar_agenda: true,
    menu_configuracoes: true,
    menu_usuarios: true,
    menu_niveis_acesso: true,
    menu_notificacoes: true,
    menu_logs: true
  });

  // Estados do diálogo de confirmação
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: 0,
    nome: ''
  });

  // Opções de níveis de acesso
  const niveisDisponiveis = [
    'ADMIN',
    'PSICOLOGO',
    'SECRETARIA'
  ];

  // Configuração dos itens do menu para exibição
  const menuItems = [
    { key: 'menu_dashboard', label: 'Dashboard', group: 'principal' },
    { key: 'menu_clientes', label: 'Clientes', group: 'principal' },
    { key: 'menu_agendamento', label: 'Agendamento', group: 'principal' },
    { key: 'menu_evolucoes', label: 'Evoluções', group: 'principal' },
    { key: 'menu_experiencia', label: 'Experiência Diária', group: 'principal' },
    { key: 'menu_financeiro', label: 'Financeiro (Menu)', group: 'financeiro' },
    { key: 'menu_recebimentos', label: 'Recebimentos', group: 'financeiro' },
    { key: 'menu_despesas', label: 'Despesas', group: 'financeiro' },
    { key: 'menu_tipos_despesa', label: 'Tipos de Despesa', group: 'financeiro' },
    { key: 'menu_raiox_financeiro', label: 'RaioX Financeiro', group: 'financeiro' },
    { key: 'menu_utilidades', label: 'Utilidades (Menu)', group: 'utilidades' },
    { key: 'menu_gerar_mensalidades', label: 'Gerar Mensalidades', group: 'utilidades' },
    { key: 'menu_gerar_agenda', label: 'Gerar Agenda', group: 'utilidades' },
    { key: 'menu_configuracoes', label: 'Configurações (Menu)', group: 'configuracoes' },
    { key: 'menu_usuarios', label: 'Usuários', group: 'configuracoes' },
    { key: 'menu_niveis_acesso', label: 'Níveis de Acesso', group: 'configuracoes' },
    { key: 'menu_notificacoes', label: 'Notificações', group: 'configuracoes' },
    { key: 'menu_logs', label: 'Logs do Sistema', group: 'configuracoes' }
  ];

  // =====================================================
  // EFEITOS
  // =====================================================

  useEffect(() => {
    carregarNiveisAcesso();
  }, []);

  // =====================================================
  // FUNÇÕES DE API
  // =====================================================

  const carregarNiveisAcesso = async () => {
    try {
      setLoading(true);
      const response = await nivelAcessoService.listar();
      
      if (response.data && response.data.success) {
        // Converter campos numéricos para boolean
        const niveisConvertidos = response.data.data.map((nivel: any) => ({
          ...nivel,
          menu_dashboard: Boolean(nivel.menu_dashboard),
          menu_clientes: Boolean(nivel.menu_clientes),
          menu_agendamento: Boolean(nivel.menu_agendamento),
          menu_evolucoes: Boolean(nivel.menu_evolucoes),
          menu_experiencia: Boolean(nivel.menu_experiencia),
          menu_financeiro: Boolean(nivel.menu_financeiro),
          menu_recebimentos: Boolean(nivel.menu_recebimentos),
          menu_despesas: Boolean(nivel.menu_despesas),
          menu_tipos_despesa: Boolean(nivel.menu_tipos_despesa),
          menu_raiox_financeiro: Boolean(nivel.menu_raiox_financeiro),
          menu_utilidades: Boolean(nivel.menu_utilidades),
          menu_gerar_mensalidades: Boolean(nivel.menu_gerar_mensalidades),
          menu_gerar_agenda: Boolean(nivel.menu_gerar_agenda),
          menu_configuracoes: Boolean(nivel.menu_configuracoes),
          menu_usuarios: Boolean(nivel.menu_usuarios),
          menu_niveis_acesso: Boolean(nivel.menu_niveis_acesso),
          menu_notificacoes: Boolean(nivel.menu_notificacoes),
          menu_logs: Boolean(nivel.menu_logs)
        }));
        setNiveisAcesso(niveisConvertidos);
      } else {
        setMessage({ type: 'error', text: 'Erro ao carregar níveis de acesso' });
      }
    } catch (error) {
      console.error('Erro ao carregar níveis de acesso:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar níveis de acesso' });
    } finally {
      setLoading(false);
    }
  };

  const salvarNivelAcesso = async () => {
    try {
      setLoading(true);
      
      // Preparar dados para envio (converter boolean para number)
      const dadosParaEnvio = {
        grupo: formData.nome,
        nivel_acesso: formData.nivel_acesso,
        detalhes: formData.descricao,
        menu_dashboard: formData.menu_dashboard ? 1 : 0,
        menu_clientes: formData.menu_clientes ? 1 : 0,
        menu_agendamento: formData.menu_agendamento ? 1 : 0,
        menu_evolucoes: formData.menu_evolucoes ? 1 : 0,
        menu_experiencia: formData.menu_experiencia ? 1 : 0,
        menu_financeiro: formData.menu_financeiro ? 1 : 0,
        menu_recebimentos: formData.menu_recebimentos ? 1 : 0,
        menu_despesas: formData.menu_despesas ? 1 : 0,
        menu_tipos_despesa: formData.menu_tipos_despesa ? 1 : 0,
        menu_raiox_financeiro: formData.menu_raiox_financeiro ? 1 : 0,
        menu_utilidades: formData.menu_utilidades ? 1 : 0,
        menu_gerar_mensalidades: formData.menu_gerar_mensalidades ? 1 : 0,
        menu_gerar_agenda: formData.menu_gerar_agenda ? 1 : 0,
        menu_configuracoes: formData.menu_configuracoes ? 1 : 0,
        menu_usuarios: formData.menu_usuarios ? 1 : 0,
        menu_niveis_acesso: formData.menu_niveis_acesso ? 1 : 0,
        menu_notificacoes: formData.menu_notificacoes ? 1 : 0,
        menu_logs: formData.menu_logs ? 1 : 0
      };

      let response;
      if (editingId) {
        response = await nivelAcessoService.atualizar(editingId, dadosParaEnvio);
      } else {
        response = await nivelAcessoService.criar(dadosParaEnvio);
      }

      if (response.data && response.data.success) {
        setMessage({ 
          type: 'success', 
          text: editingId ? 'Nível de acesso atualizado com sucesso!' : 'Nível de acesso criado com sucesso!' 
        });
        await carregarNiveisAcesso();
        voltarParaLista();
      } else {
        setMessage({ type: 'error', text: response.data?.message || 'Erro ao salvar nível de acesso' });
      }
    } catch (error) {
      console.error('Erro ao salvar nível de acesso:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar nível de acesso' });
    } finally {
      setLoading(false);
    }
  };

  const excluirNivelAcesso = async (id: number) => {
    try {
      setLoading(true);
      const response = await nivelAcessoService.excluir(id);
      
      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: 'Nível de acesso excluído com sucesso!' });
        await carregarNiveisAcesso();
      } else {
        setMessage({ type: 'error', text: 'Erro ao excluir nível de acesso' });
      }
    } catch (error) {
      console.error('Erro ao excluir nível de acesso:', error);
      setMessage({ type: 'error', text: 'Erro ao excluir nível de acesso' });
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, id: 0, nome: '' });
    }
  };

  // =====================================================
  // FUNÇÕES DE INTERFACE
  // =====================================================

  const abrirFormulario = (nivel?: NivelAcesso) => {
    if (nivel) {
      setEditingId(nivel.id);
      setFormData({
        nome: nivel.nome,
        nivel_acesso: nivel.nivel_acesso,
        descricao: nivel.descricao,
        menu_dashboard: nivel.menu_dashboard,
        menu_clientes: nivel.menu_clientes,
        menu_agendamento: nivel.menu_agendamento,
        menu_evolucoes: nivel.menu_evolucoes,
        menu_experiencia: nivel.menu_experiencia,
        menu_financeiro: nivel.menu_financeiro,
        menu_recebimentos: nivel.menu_recebimentos,
        menu_despesas: nivel.menu_despesas,
        menu_tipos_despesa: nivel.menu_tipos_despesa,
        menu_raiox_financeiro: nivel.menu_raiox_financeiro,
        menu_utilidades: nivel.menu_utilidades,
        menu_gerar_mensalidades: nivel.menu_gerar_mensalidades,
        menu_gerar_agenda: nivel.menu_gerar_agenda,
        menu_configuracoes: nivel.menu_configuracoes,
        menu_usuarios: nivel.menu_usuarios,
        menu_niveis_acesso: nivel.menu_niveis_acesso,
        menu_notificacoes: nivel.menu_notificacoes,
        menu_logs: nivel.menu_logs
      });
    } else {
      setEditingId(null);
      setFormData({
        nome: '',
        nivel_acesso: '',
        descricao: '',
        menu_dashboard: true,
        menu_clientes: true,
        menu_agendamento: true,
        menu_evolucoes: true,
        menu_experiencia: true,
        menu_financeiro: true,
        menu_recebimentos: true,
        menu_despesas: true,
        menu_tipos_despesa: true,
        menu_raiox_financeiro: true,
        menu_utilidades: true,
        menu_gerar_mensalidades: true,
        menu_gerar_agenda: true,
        menu_configuracoes: true,
        menu_usuarios: true,
        menu_niveis_acesso: true,
        menu_notificacoes: true,
        menu_logs: true
      });
    }
    setShowForm(true);
  };

  const voltarParaLista = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Função para obter cor do chip baseada no nível
  const getCorNivel = (nivel: string) => {
    switch (nivel) {
      case 'ADMIN': return 'error';
      case 'PSICOLOGO': return 'success';
      case 'SECRETARIA': return 'primary';
      default: return 'default';
    }
  };

  // Função para obter label amigável do nível
  const getLabelNivel = (nivel: string) => {
    switch (nivel) {
      case 'ADMIN': return 'Administrador';
      case 'PSICOLOGO': return 'Psicólogo';
      case 'SECRETARIA': return 'Secretária';
      default: return nivel;
    }
  };

  // Função para agrupar itens do menu
  const getMenuItemsByGroup = (group: string) => {
    return menuItems.filter(item => item.group === group);
  };

  // Função para contar menus ativos
  const contarMenusAtivos = (nivel: NivelAcesso) => {
    const campos = [
      'menu_dashboard', 'menu_clientes', 'menu_agendamento', 'menu_evolucoes', 'menu_experiencia',
      'menu_financeiro', 'menu_recebimentos', 'menu_despesas', 'menu_tipos_despesa', 'menu_raiox_financeiro',
      'menu_utilidades', 'menu_gerar_mensalidades', 'menu_gerar_agenda',
      'menu_configuracoes', 'menu_usuarios', 'menu_niveis_acesso', 'menu_notificacoes', 'menu_logs'
    ];
    return campos.filter(campo => nivel[campo as keyof NivelAcesso]).length;
  };

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================

  if (showForm) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={voltarParaLista} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            {editingId ? 'Editar Nível de Acesso' : 'Novo Nível de Acesso'}
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {/* Dados Básicos */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>
                  Dados Básicos
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nome do Grupo"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Nível de Acesso</InputLabel>
                  <Select
                    name="nivel_acesso"
                    value={formData.nivel_acesso}
                    onChange={handleSelectChange}
                    label="Nível de Acesso"
                  >
                    {niveisDisponiveis.map(nivel => (
                      <MenuItem key={nivel} value={nivel}>
                        <Chip 
                          label={getLabelNivel(nivel)} 
                          color={getCorNivel(nivel) as any}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {getLabelNivel(nivel)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descrição"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  multiline
                  rows={1}
                />
              </Grid>

              {/* Controles de Menu */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>
                  Controle de Visibilidade do Menu
                </Typography>
              </Grid>

              {/* Menu Principal */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 1.5, height: 'fit-content' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    Menu Principal
                  </Typography>
                  <FormGroup>
                    {getMenuItemsByGroup('principal').map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            size="small"
                            name={item.key}
                            checked={formData[item.key as keyof MenuPermissions]}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label={<Typography sx={{ fontSize: '0.75rem' }}>{item.label}</Typography>}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              </Grid>

              {/* Menu Financeiro */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 1.5, height: 'fit-content' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    Menu Financeiro
                  </Typography>
                  <FormGroup>
                    {getMenuItemsByGroup('financeiro').map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            size="small"
                            name={item.key}
                            checked={formData[item.key as keyof MenuPermissions]}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label={<Typography sx={{ fontSize: '0.75rem' }}>{item.label}</Typography>}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              </Grid>

              {/* Menu Utilidades */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 1.5, height: 'fit-content' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    Menu Utilidades
                  </Typography>
                  <FormGroup>
                    {getMenuItemsByGroup('utilidades').map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            size="small"
                            name={item.key}
                            checked={formData[item.key as keyof MenuPermissions]}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label={<Typography sx={{ fontSize: '0.75rem' }}>{item.label}</Typography>}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              </Grid>

              {/* Menu Configurações */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 1.5, height: 'fit-content' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    Menu Configurações
                  </Typography>
                  <FormGroup>
                    {getMenuItemsByGroup('configuracoes').map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            size="small"
                            name={item.key}
                            checked={formData[item.key as keyof MenuPermissions]}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label={<Typography sx={{ fontSize: '0.75rem' }}>{item.label}</Typography>}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              </Grid>

              {/* Botões */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={voltarParaLista}
                    disabled={loading}
                  >
                    Voltar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={salvarNivelAcesso}
                    disabled={loading || !formData.nome || !formData.nivel_acesso}
                  >
                    {loading ? 'Salvando...' : 'Gravar'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} color="primary" sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" gutterBottom>
          Níveis de Acesso
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Gerenciar Níveis de Acesso
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => abrirFormulario()}
              disabled={loading}
            >
              Novo Nível
            </Button>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Nome do Grupo</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Nível</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Descrição</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Menus Ativos</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {niveisAcesso.map((nivel) => (
                  <TableRow key={nivel.id} hover>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{nivel.id}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{nivel.nome}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getLabelNivel(nivel.nivel_acesso)} 
                        color={getCorNivel(nivel.nivel_acesso) as any}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>
                      {nivel.descricao || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <Chip 
                        label={`${contarMenusAtivos(nivel)}/18`}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => abrirFormulario(nivel)}
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDialog({ open: true, id: nivel.id, nome: nivel.nome })}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={() => setMessage({ type: '', text: '' })}
      >
        <Alert 
          onClose={() => setMessage({ type: '', text: '' })} 
          severity={message.type as any}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NiveisAcesso;
